import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  FormGroup,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MDBoxRoot from './MDBox/MDBoxRoot';
import MDButtonRoot from './MDButton/MDButtonRoot';
import MDTypographyRoot from './MDTypography/MDTypographyRoot';
import { useScreenSize } from "../hooks/ScreenSizeContext";
import { SchoolTime } from '../utils/SchoolTime';


export type CreateSeriesFormData = {
  repeatType: string;
  numWeeks?: number;
  endDate?: string;
  selectedDays: boolean[];
};

type SelectedDays = {
  Sunday: boolean;
  Monday: boolean;
  Tuesday: boolean;
  Wednesday: boolean;
  Thursday: boolean;
  Friday: boolean;
  Saturday: boolean;
};

type CreateSeriesFormDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (formData: CreateSeriesFormData) => void;
  date?: string;
};

const CreateSeriesDialog: React.FC<CreateSeriesFormDialogProps> = ({
  open,
  onClose,
  onSave,
  date
}) => {
  const isSmallScreen = useScreenSize();
  const [repeatType, setRepeatType] = useState<string>('weeks');
  const [numWeeks, setNumWeeks] = useState<number>(1);
  const [endDate, setEndDate] = useState<string>(date?.split('T')[0]);
  const [selectedDays, setSelectedDays] = useState<SelectedDays>({
    Sunday: false,
    Monday: false,
    Tuesday: false,
    Wednesday: false,
    Thursday: false,
    Friday: false,
    Saturday: false
  });

  const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const currDate = SchoolTime.fromISO(date);
  useEffect(() => {
    if (date && selectedDays.hasOwnProperty(currDate.toDateTime().toFormat("cccc"))) {
      setSelectedDays(prev => ({ ...prev, [currDate.toDateTime().toFormat("cccc")]: true }));
    }
  }, [date]);

  const handleDayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDays({ ...selectedDays, [event.target.name]: event.target.checked });
  };

  const handleRepeatTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRepeatType(event.target.value);
  };

  const handleSubmit = () => {
    const formData = {
      repeatType,
      numWeeks: repeatType === 'weeks' ? numWeeks : undefined,
      endDate: repeatType === 'endDate' ? endDate : undefined,
      selectedDays: daysOfWeek.map(day => selectedDays[day as keyof SelectedDays] || false)
    };
    onSave(formData);
    onClose();
  };

  return (
    <Dialog maxWidth="sm"
      onClose={onClose}
      open={open}
      fullWidth>
      <MDTypographyRoot variant="h5" px={1} py={1}>Replicate Event
        <CloseIcon onClick={onClose} sx={{ position: "absolute", top: "10px", right: "10px" }} />
      </MDTypographyRoot>
      <DialogContent dividers>
        <FormControl component="fieldset" fullWidth margin="normal">
          <RadioGroup
            row
            value={repeatType}
            onChange={handleRepeatTypeChange}
          >
            <FormControlLabel value="weeks" control={<Radio />} label="Repeat by Number of Weeks" />
            <FormControlLabel value="endDate" control={<Radio />} label="Repeat until End Date" />
          </RadioGroup>
        </FormControl>
        {repeatType === 'weeks' && (
          <TextField
            label="Number of Weeks"
            type="number"
            value={numWeeks}
            onChange={(e) => setNumWeeks(Number(e.target.value))}
            fullWidth
            margin="normal"
          />
        )}
        {repeatType === 'endDate' && (
          <TextField
            label="End Date"
            variant="filled"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="normal"
          />
        )}
        <FormGroup row={!isSmallScreen}>
          {Object.keys(selectedDays).map((day) => (
            <FormControlLabel
              key={day}
              control={
                <Checkbox
                  checked={selectedDays[day as keyof SelectedDays]}
                  onChange={handleDayChange}
                  name={day}
                />
              }
              label={day}
            />
          ))}
        </FormGroup>
      </DialogContent>
      <DialogActions >
        <MDBoxRoot
          display="flex"
          justifyContent="center"
          alignItems="baseline"
          flexDirection="row"
          gap="16px"
          width="100%"
        >
          <MDButtonRoot
            size="medium"
            ownerState={{
              textTransform: "none",
              variant: "gradient",
              color: "warning",
            }}
            onClick={onClose}>
            Cancel</MDButtonRoot>

          <MDButtonRoot
            ownerState={{
              variant: "gradient",
              color: "info",
              textTransform: "none",
            }}
            onClick={handleSubmit}>
            Save </MDButtonRoot>
        </MDBoxRoot>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSeriesDialog;
